
// Service to handle image conversion, compression and resizing
// We use heic2any for HEIC support as browsers don't support it natively
export interface CompressOptions {
  /**
   * Querformat auf einen zentralen 3:4-Hochformat-Ausschnitt beschneiden.
   *
   * Voreinstellung true, weil das fuer Begehungsfotos gedacht ist und dort seit
   * jeher so laeuft. Fuer alles, was KEIN Foto ist — Logos, Plaene, Scans —
   * muss es false sein: der Zuschnitt wirft bei einem breiten Bild alles ausser
   * dem Mittelstreifen weg, und zwar unwiederbringlich schon beim Hochladen.
   */
  cropToPortrait?: boolean;
  /**
   * Transparenz erhalten: PNG/WebP/GIF ohne weisse Unterlage als PNG ausgeben.
   *
   * Voreinstellung false — Fotos sollen klein als JPEG gespeichert werden. Fuer
   * Logos muss es true sein: sonst traegt ein freigestelltes Firmenlogo ab dem
   * Hochladen ein weisses Rechteck, das auf farbigem Plankopf/Briefbogen steht.
   */
  keepTransparency?: boolean;
}

/** Nur Formate mit Alphakanal lohnen die groessere PNG-Ausgabe. */
export const behaeltTransparenz = (file: File, isHeic: boolean): boolean =>
  !isHeic && (/^image\/(png|webp|gif)$/i.test(file.type) || /\.(png|webp|gif)$/i.test(file.name));

/**
 * Dateiendungen und MIME-Typen, die iPhone/iPad und neuere Android-Kameras fuer
 * HEIC/HEIF liefern. `.hif` kommt von Fujifilm-Kameras.
 */
export const HEIC_ACCEPT = '.heic,.heif,.hif,image/heic,image/heif';

/** ISO-BMFF-Marken, die einen HEIC/HEIF-Container kennzeichnen (Box `ftyp`). */
const HEIC_MARKEN = new Set([
  'heic', 'heix', 'heim', 'heis', 'hevc', 'hevx', 'hevm', 'hevs', 'mif1', 'msf1',
]);

/**
 * Erkennt HEIC/HEIF-Dateien. Endung und MIME-Typ reichen nicht: Windows und
 * einige Android-Galerien reichen die Datei ohne Typ (oder als
 * application/octet-stream) durch, teils sogar ohne Endung. Dann entscheidet
 * der Dateikopf — ohne ihn landet ein iPhone-Foto ungewandelt im Canvas-Loader
 * und der Upload scheitert kommentarlos.
 */
export const istHeicDatei = async (file: File | Blob): Promise<boolean> => {
  const name = (file instanceof File ? file.name : '').toLowerCase();
  if (/\.(heic|heif|hif)$/.test(name)) return true;

  const typ = (file.type || '').toLowerCase();
  if (typ.startsWith('image/heic') || typ.startsWith('image/heif')) return true;
  // Ein aussagekraeftiger Typ (image/jpeg, application/pdf, ...) schliesst HEIC aus.
  if (typ && typ !== 'application/octet-stream') return false;

  try {
    const kopf = new Uint8Array(await file.slice(0, 12).arrayBuffer());
    if (kopf.length < 12) return false;
    const box = String.fromCharCode(...kopf.subarray(4, 8));
    if (box !== 'ftyp') return false;
    const marke = String.fromCharCode(...kopf.subarray(8, 12)).toLowerCase();
    return HEIC_MARKEN.has(marke);
  } catch {
    return false;
  }
};

/**
 * Eigener Fehlertyp, damit die Oberflaeche den HEIC-Fall benennen kann statt
 * "Fehler beim Verarbeiten des Bildes" — der Anwender kann sonst nicht wissen,
 * dass ein zweiter Versuch mit Netz oder ein JPEG-Foto hilft.
 */
export class HeicConversionError extends Error {
  constructor(public readonly texte: { de: string; en: string }, public readonly grund = '') {
    super(grund ? `${texte.de} (${grund})` : texte.de);
    this.name = 'HeicConversionError';
  }
}

/**
 * Fehlermeldung fuer die Anzeige, mit HEIC-Hinweis wenn es einer war. Der
 * technische Grund haengt hinten dran: ohne ihn ist ein Fehlschlag beim
 * Anwender auf der Baustelle nicht zu klaeren.
 */
export const bildFehlerText = (err: unknown, sprache: 'de' | 'en', fallback: string): string => {
  if (!(err instanceof HeicConversionError)) return fallback;
  return err.grund ? `${err.texte[sprache]}\n\n(${err.grund})` : err.texte[sprache];
};

/**
 * Fehlerursache auf eine Zeile bringen, die in einen Alert passt. libheif wirft
 * schlichte Objekte statt Error ({ code, message }) — ohne diesen Zweig stand
 * dort "[object Object]" und der Fehlschlag war nicht zu klaeren.
 */
export const kurzgrund = (err: unknown): string => {
  let text: string;
  if (err instanceof Error) {
    text = `${err.name}: ${err.message}`;
  } else if (err && typeof err === 'object') {
    const o = err as { message?: unknown; code?: unknown };
    if (typeof o.message === 'string') {
      text = o.code === undefined ? o.message : `${o.message} (code ${String(o.code)})`;
    } else {
      try {
        text = JSON.stringify(err);
      } catch {
        text = Object.prototype.toString.call(err);
      }
    }
  } else {
    text = String(err ?? 'unbekannt');
  }
  const eineZeile = text.replace(/\s+/g, ' ').trim();
  return eineZeile.length > 160 ? `${eineZeile.slice(0, 160)}…` : eineZeile;
};

interface HeifBild {
  get_width: () => number;
  get_height: () => number;
  display: (daten: ImageData, fertig: (ergebnis: ImageData | null) => void) => void;
  free?: () => void;
}

interface LibheifModul {
  HeifDecoder: new () => { decode: (daten: Uint8Array) => HeifBild[] };
}

let libheifCache: Promise<LibheifModul> | null = null;

/**
 * libheif-js statt heic2any: dessen eingebackenes libheif ist von 2020 und
 * lehnt Fotos aktueller iPhones mit "ERR_LIBHEIF format not supported" ab.
 * Geladen wird das WASM-Bundle (Binaerteil inline, also keine zweite
 * Anfrage) — als dynamischer Import bleibt es ein eigener Chunk, den nur der
 * HEIC-Fall herunterlaedt.
 */
const ladeLibheif = (): Promise<LibheifModul> => {
  if (libheifCache) return libheifCache;
  libheifCache = (async () => {
    const modul: unknown = await import('libheif-js/libheif-wasm/libheif-bundle.mjs');
    const fabrik = (modul as { default?: unknown })?.default ?? modul;
    const lib = (typeof fabrik === 'function'
      ? await (fabrik as () => LibheifModul | Promise<LibheifModul>)()
      : fabrik) as LibheifModul;
    if (typeof lib?.HeifDecoder !== 'function') throw new Error('libheif: unerwartetes Modulformat');
    return lib;
  })();
  // Ein fehlgeschlagener Ladeversuch (Funkloch) darf den naechsten nicht blockieren.
  libheifCache.catch(() => { libheifCache = null; });
  return libheifCache;
};

/** HEIC dekodieren und als JPEG-Blob zurueckgeben, den der Browser selbst lesen kann. */
const heicZuJpeg = async (file: File): Promise<Blob> => {
  const libheif = await ladeLibheif();
  const bilder = new libheif.HeifDecoder().decode(new Uint8Array(await file.arrayBuffer()));
  if (!bilder || bilder.length === 0) throw new Error('Die Datei enthaelt kein lesbares HEIC-Bild');

  // Ein Live Photo bringt mehrere Bilder mit; das erste ist das Standbild.
  const bild = bilder[0];
  try {
    const breite = bild.get_width();
    const hoehe = bild.get_height();
    const canvas = document.createElement('canvas');
    canvas.width = breite;
    canvas.height = hoehe;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Kein Canvas-Kontext fuer die HEIC-Ausgabe');

    const daten = ctx.createImageData(breite, hoehe);
    await new Promise<void>((resolve, reject) => {
      bild.display(daten, (ergebnis) => {
        if (ergebnis) resolve();
        else reject(new Error('libheif konnte die Bilddaten nicht ausgeben'));
      });
    });
    ctx.putImageData(daten, 0, 0);

    return await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error('Canvas lieferte kein JPEG'))),
        'image/jpeg',
        0.9
      );
    });
  } finally {
    // Das dekodierte Vollbild liegt im WASM-Speicher — bei 12 Megapixeln sind
    // das rund 48 MB, die auf dem Telefon sonst bis zum Seitenwechsel bleiben.
    bild.free?.();
  }
};

export const compressImage = async (
  file: File,
  maxWidth = 800,
  quality = 0.55,
  opts: CompressOptions = {}
): Promise<string> => {
  const { cropToPortrait = true, keepTransparency = false } = opts;
  let processingFile: File | Blob = file;

  // 1. HEIC Conversion if needed
  const isHeic = await istHeicDatei(file);
  let heicGrund: string | null = null;
  const transparent = keepTransparency && behaeltTransparenz(file, isHeic);

  if (isHeic) {
    try {
      const umgewandelt = await heicZuJpeg(file);
      // Ein leerer Blob wuerde erst spaeter als nichtssagender Ladefehler
      // auffallen — hier ist die Ursache noch bekannt.
      if (umgewandelt.size === 0) throw new Error('Decoder lieferte ein leeres Bild');
      processingFile = umgewandelt;
    } catch (e) {
      // Safari dekodiert HEIC nativ — dort traegt der Standard-Loader unten noch.
      // Ueberall sonst scheitert er, und erst dann melden wir den HEIC-Grund.
      heicGrund = kurzgrund(e);
      console.error("HEIC conversion failed, trying standard loader", e);
    }
  }

  return new Promise((resolve, reject) => {
    const scheitern = (err: unknown) => {
      if (heicGrund) {
        reject(new HeicConversionError({
          de: 'HEIC-Foto konnte nicht umgewandelt werden. Bitte die Seite neu laden und erneut versuchen — oder am iPhone unter Einstellungen › Kamera › Formate auf "Maximale Kompatibilität" (JPEG) stellen.',
          en: 'Could not convert the HEIC photo. Please reload the page and try again, or switch the iPhone to Settings › Camera › Formats › "Most Compatible" (JPEG).',
        }, heicGrund));
        return;
      }
      reject(err instanceof Error ? err : new Error('Bild konnte nicht gelesen werden'));
    };

    const reader = new FileReader();
    reader.readAsDataURL(processingFile);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target?.result as string;
      img.onload = () => {
        const elem = document.createElement('canvas');
        let sourceX = 0;
        let sourceY = 0;
        let sourceWidth = img.width;
        let sourceHeight = img.height;

        // Auto-Crop Landscape (Querformat) images to target central Portrait format (3:4 ratio)
        if (cropToPortrait && img.width > img.height) {
            sourceHeight = img.height;
            sourceWidth = img.height * (3 / 4);
            sourceX = (img.width - sourceWidth) / 2;
        }

        let width = sourceWidth;
        let height = sourceHeight;

        // Resize logic maintaining aspect ratio
        if (width > maxWidth || height > maxWidth) {
            if (width > height) {
                height *= maxWidth / width;
                width = maxWidth;
            } else {
                width *= maxWidth / height;
                height = maxWidth;
            }
        }

        elem.width = width;
        elem.height = height;
        const ctx = elem.getContext('2d');
        if (!ctx) {
            reject(new Error("Could not get canvas context"));
            return;
        }

        if (transparent) {
            ctx.drawImage(img, sourceX, sourceY, sourceWidth, sourceHeight, 0, 0, width, height);
            resolve(elem.toDataURL('image/png'));
            return;
        }

        // Fill background with white (for transparent PNGs or converted HEICs)
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, width, height);

        ctx.drawImage(img, sourceX, sourceY, sourceWidth, sourceHeight, 0, 0, width, height);

        // Compress to JPEG
        const dataUrl = elem.toDataURL('image/jpeg', quality);
        resolve(dataUrl);
      };
      img.onerror = (err) => scheitern(err);
    };
    reader.onerror = (err) => scheitern(err);
  });
};
